// Copyright ©️ 2026 eVolve MEP, LLC

import type { ReactNode } from 'react';

import { Button, Flex, Input, PasswordInput, Text, useMantineTheme } from '@mantine/core';
import type { UseFormReturnType } from '@mantine/form';
import { Link } from 'react-router';

import { isNil } from 'helpers/isNotNil';

import type { SignInFormValues } from '../auth/useSignInSteps';

type Props = {
  form: UseFormReturnType<SignInFormValues>;
  busy: boolean;
  submitLabel: string;
  /** Rendered opposite the Forgot Password link. */
  options?: ReactNode;
  onChangeLogin: () => void;
};

/** The password step of the sign-in steps from `useSignInSteps`, with Forgot Password and Change login links. */
export const PasswordStepFields = ({ form, busy, submitLabel, options, onChangeLogin }: Props) => {
  const theme = useMantineTheme();
  return (
    <>
      <Input.Label mb={-10} size="md">
        Email
      </Input.Label>
      <Text c="gray.9">{form.values.email}</Text>
      <PasswordInput
        label="Password"
        data-cy="password-input"
        name="password"
        size="md"
        disabled={busy}
        autoFocus
        {...form.getInputProps('password')}
      />
      <Flex my="lg" justify={isNil(options) ? 'flex-end' : 'space-between'} align="center">
        {options}
        <Link
          className="link"
          data-cy="forgot-password"
          to="/auth/forgot-password"
          state={{ email: form.values.email }}
        >
          <Text c={busy ? 'dimmed' : theme.primaryColor}>Forgot Password?</Text>
        </Link>
      </Flex>
      <Button type="submit" loading={busy} disabled={!form.isValid()} fullWidth color={`${theme.primaryColor}.9`}>
        {submitLabel}
      </Button>
      <Flex justify="center" align="center" gap={4}>
        <Text fz="sm" c="dimmed">
          Wrong email?
        </Text>
        <Text fz="sm" c={busy ? 'dimmed' : undefined} className="link" onClick={onChangeLogin}>
          Change login
        </Text>
      </Flex>
    </>
  );
};
